import React, { useState } from 'react'
import { Box, IconButton, Tab, Tabs } from '@mui/material'
import { visuallyHidden } from '@mui/utils'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import ArrowForwardIcon from '@mui/icons-material/ArrowForward'
import VehiclesTab from './VehiclesTab'
import DriversTab from './DriversTab'

interface SchedulesVehiclesDriversPanelProps {
	collapsed: boolean
	contentVisible?: boolean
	onToggle: () => void
	onContentTransitionEnd?: () => void
	onManageDriversClick?: () => void
	onManageVehiclesClick?: () => void
	canAssign?: boolean
	onAssignDriver?: (driverId: number) => void
	onAssignVehicle?: (vehicleId: number) => void
}

const SchedulesVehiclesDriversPanel = React.memo(function SchedulesVehiclesDriversPanel({
	collapsed,
	contentVisible,
	onToggle,
	onContentTransitionEnd,
	onManageDriversClick,
	onManageVehiclesClick,
	canAssign = false,
	onAssignDriver,
	onAssignVehicle,
}: SchedulesVehiclesDriversPanelProps) {
	const [tab, setTab] = useState(0)
	const isContentVisible = contentVisible ?? !collapsed
	const isPanelCollapsed = collapsed || !isContentVisible

	const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
		setTab(newValue)
	}

	return (
		<Box
			sx={{
				position: 'relative',
				height: '100%',
				backgroundColor: 'background.paper',
				borderRadius: '14px',
				padding: collapsed ? '8px' : '16px',
				display: 'flex',
				flexDirection: 'column',
				overflow: 'visible',
				transition: 'padding 0.35s ease-in-out',
			}}
		>
			<IconButton
				size='small'
				onClick={onToggle}
				sx={{
					position: 'absolute',
					left: collapsed ? '8px' : '-14px',
					right: 'auto',
					top: collapsed ? '8px' : '12px',
					backgroundColor: '#FFFFFF',
					border: '1px solid rgba(134, 149, 175, 0.2)',
					boxShadow: 'none',
					width: '28px',
					height: '28px',
					zIndex: 2,
					'&:hover': {
						backgroundColor: '#FFFFFF',
					},
				}}
			>
				{isPanelCollapsed ? (
					<ArrowBackIcon sx={{ color: '#00A5FF' }} />
				) : (
					<ArrowForwardIcon sx={{ color: '#00A5FF' }} />
				)}
				<span style={visuallyHidden}>{isPanelCollapsed ? 'Expandir navegação' : 'Fechar navegação'}</span>
			</IconButton>

			<Box sx={{ flex: 1, minHeight: 0, overflow: 'hidden' }}>
				<Box
					sx={{
						display: 'flex',
						flexDirection: 'column',
						height: '100%',
						minHeight: 0,
						width: '100%',
						transform: isContentVisible ? 'translateX(0)' : 'translateX(120%)',
						opacity: isContentVisible ? 1 : 0,
						pointerEvents: isContentVisible ? 'auto' : 'none',
						transition: 'transform 0.35s ease-in-out, opacity 0.2s ease-in-out',
					}}
					onTransitionEnd={event => {
						if (event.propertyName !== 'transform') {
							return
						}
						onContentTransitionEnd?.()
					}}
				>
					<Tabs
						value={tab}
						onChange={handleTabChange}
						sx={{
							display: 'flex',
							justifyContent: 'center',
							'& .MuiTab-root': {
								fontSize: '14px',
								color: '#8695AF',
								fontWeight: 600,
								textTransform: 'none',
								'&.Mui-selected': {
									color: '#00A5FF',
								},
							},
							'& .MuiTabs-flexContainer': {
								justifyContent: 'center',
							},
						}}
					>
						<Tab label='Viaturas' />
						<Tab label='Motoristas' />
					</Tabs>
					<Box sx={{ flex: 1, minHeight: 0, marginTop: '16px' }}>
						{tab === 0 ? (
							<VehiclesTab
								onManageVehiclesClick={onManageVehiclesClick}
								canAssign={canAssign}
								onAssignVehicle={onAssignVehicle}
							/>
						) : (
							<DriversTab
								onManageDriversClick={onManageDriversClick}
								canAssign={canAssign}
								onAssignDriver={onAssignDriver}
							/>
						)}
					</Box>
				</Box>
			</Box>
		</Box>
	)
})

export default SchedulesVehiclesDriversPanel
